import { FC, useState, useEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { useDropdown } from '@/utils/dashboard/useDropdown';
import { IMAGE_MODELS } from '@/constants/models';

type MobileVideoPreviewProps = Readonly<{
  previewUrl: string;
  onRemove: () => void;
  fileName?: string;
  fileSize?: string;
}>;

export function MobileVideoPreview({
  previewUrl,
  onRemove,
  fileName = "Video file.mp4",
  fileSize = "1 GB",
}: MobileVideoPreviewProps) {
  return (
    <div className="w-full max-w-sm mx-auto rounded-xl p-3 flex items-center justify-between" style={{ backgroundColor: '#F0F7FF' }}>
      <div className="flex items-center gap-3">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          <video
            src={previewUrl}
            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
            controls={false}
            muted
          >
            <track kind="captions" src="" label="No captions available" />
          </video>
        </div>

        {/* File info */}
        <div className="flex flex-col gap-1 items-start min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {fileName}
          </div>
          <div className="text-sm text-gray-500">
            {fileSize}
          </div>
        </div>
      </div>

      {/* Remove button */}
      {onRemove && (
        <button
          onClick={onRemove}
          className="flex-shrink-0 p-2 transition-colors bg-transparent text-gray-500"
          aria-label="Remove image"
        >
          <DeleteOutlinedIcon />
        </button>
      )}
    </div>
  )
}

type MobileStackedImagesProps = Readonly<{
  imagePreviews: readonly string[];
  totalImages?: number;
  onRemoveImage?: (index: number) => void;
  onAddImage?: () => void;
}>;

export function MobileStackedImages({
  imagePreviews,
  totalImages = 3,
  onRemoveImage,
  onAddImage,
}: MobileStackedImagesProps) {
  const imageCount = imagePreviews.length;

  return (
    <div className="w-full py-3">
      <div className="flex items-center gap-3 w-full justify-between">
        {/* Existing Images */}
        {imagePreviews.map((preview, index) => (
          <div key={`mobile-image-${index}-${preview.substring(0, 50)}`} className="flex items-center gap-2 border p-2 rounded-xl" style={{ backgroundColor: '#F0F7FF' }}>
            <div className="relative">
              <img
                src={preview}
                alt={`Selected input ${index + 1}`}
                className="w-[60px] h-[45px] object-cover rounded-xl border-2 border-gray-300"
              />
            </div>
            {/* Delete button positioned to the right */}
            {onRemoveImage && (
              <button
                onClick={() => onRemoveImage(index)}
                className="w-8 h-8 bg-transparent rounded-lg flex items-center justify-center transition-colors"
                aria-label={`Remove image ${index + 1}`}
              >
                <DeleteOutlinedIcon className="text-black" />
              </button>
            )}
          </div>
        ))}

        {/* Add Image Slots */}
        {Array.from({ length: Math.max(0, totalImages - imageCount) }, (_, index) => (
          <button
            key={`add-${index}`}
            onClick={onAddImage}
            className="w-[105px] h-[60px] flex-shrink-0 flex items-center justify-center rounded-xl border-2 bg-[#E8EAFB] hover:bg-blue-100 transition-colors"
            aria-label="Add image"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="text-[#939BEC]"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ))}
      </div>
    </div>
  )
}

type MobileImagePreviewProps = Readonly<{
  imagePreview: string;
  onRemove?: () => void;
  imageCount?: number;
  totalImages?: number;
  fileName?: string;
  fileSize?: string;
}>;

export function MobileImagePreview({
  imagePreview,
  onRemove,
  imageCount,
  totalImages,
  fileName = "Image file.png",
  fileSize = "10 MB",
}: MobileImagePreviewProps) {
  return (
    <div className="w-full max-w-sm mx-auto rounded-xl p-3 flex items-center justify-between" style={{ backgroundColor: '#F0F7FF' }}>
      <div className="flex items-center gap-3">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          <img
            src={imagePreview}
            alt="Selected input"
            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
          />
        </div>

        {/* File info */}
        <div className="flex flex-col gap-1 items-start min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {fileName}
          </div>
          <div className="text-sm text-gray-500">
            {fileSize}
          </div>
        </div>
      </div>

      {/* Remove button */}
      {onRemove && (
        <button
          onClick={onRemove}
          className="flex-shrink-0 p-2 transition-colors bg-transparent text-gray-500"
          aria-label="Remove image"
        >
          <DeleteOutlinedIcon />
        </button>
      )}

      {/* Image counter for multiple images */}
      {imageCount !== undefined && totalImages !== undefined && (
        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-[#F39A81] to-[#959BEA] text-white rounded-full px-2 py-1 text-xs font-medium flex items-center gap-1 shadow-lg z-10">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
          </svg>
          {imageCount}/{totalImages}
        </div>
      )}
    </div>
  )
}

export const MobilePromptSettingsDrawer: FC<{
  isOpen: boolean;
  onClose: () => void;
  aspectRatio: string;
  onAspectRatioChange?: (ratio: string) => void;
  imageResolution: string;
  onImageResolutionChange?: (resolution: string) => void;
  generateAudio: boolean;
  isAudioEnabled: boolean;
  onAudioToggle: (() => void) | undefined;
  selectedModel: string;
  transcription: any;
}> = ({
  isOpen,
  onClose,
  aspectRatio,
  onAspectRatioChange,
  imageResolution,
  onImageResolutionChange,
  generateAudio,
  isAudioEnabled,
  onAudioToggle,
  selectedModel,
  transcription
}) => {
    const [tempAspectRatio, setTempAspectRatio] = useState(aspectRatio);
    const [tempImageResolution, setTempImageResolution] = useState(imageResolution);
    const [tempAudioEnabled, setTempAudioEnabled] = useState(generateAudio);

    const handleApply = () => {
      onAspectRatioChange?.(tempAspectRatio);
      onImageResolutionChange?.(tempImageResolution);
      if (tempAudioEnabled !== generateAudio && onAudioToggle) {
        onAudioToggle();
      }
      onClose();
    };

    const handleReset = () => {
      const isImageModel = IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]);
      setTempAspectRatio(isImageModel ? '1:1' : '16:9');
      setTempImageResolution('1k');
      setTempAudioEnabled(false);
    };

    const handleClose = () => {
      // Reset temp values to current values when closing without applying
      setTempAspectRatio(aspectRatio);
      setTempImageResolution(imageResolution);
      setTempAudioEnabled(generateAudio);
      onClose();
    };

    // Update temp values when props change
    useEffect(() => {
      setTempAspectRatio(aspectRatio);
      setTempImageResolution(imageResolution);
      setTempAudioEnabled(generateAudio);
    }, [aspectRatio, imageResolution, generateAudio]);

    // Determine aspect ratios based on model type
    const isImageModel = IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]);
    
    const aspectRatios = isImageModel ? [
      
      // Landscape
      { value: '21:9', label: '21:9', category: 'Landscape' },
      { value: '16:9', label: '16:9', category: 'Landscape' },
      { value: '5:4', label: '5:4', category: 'Landscape' },
      { value: '4:3', label: '4:3', category: 'Landscape' },
      { value: '3:2', label: '3:2', category: 'Landscape' },
      // Portrait
      { value: '2:3', label: '2:3', category: 'Portrait' },
      { value: '3:4', label: '3:4', category: 'Portrait' },
      { value: '4:5', label: '4:5', category: 'Portrait' },
      { value: '9:16', label: '9:16', category: 'Portrait' },
      // Square
      { value: '1:1', label: '1:1', category: 'Square' },
    ] : [
      // For video models, only show 9:16 and 16:9
      { value: '9:16', label: '9:16', category: 'Portrait' },
      { value: '16:9', label: '16:9', category: 'Landscape' },
    ];

    const squareRatios = aspectRatios.filter(ratio => ratio.category === 'Square');
    const portraitRatios = aspectRatios.filter(ratio => ratio.category === 'Portrait');
    const landscapeRatios = aspectRatios.filter(ratio => ratio.category === 'Landscape');

    const renderAspectRatioButton = useCallback((ratio: { value: string; label: string; category: string }) => {
      const isSelected = tempAspectRatio === ratio.value;
      const isLandscape = ratio.category === 'Landscape';

      return (
        <button
          key={ratio.value}
          onClick={() => setTempAspectRatio(ratio.value)}
          className="flex items-center gap-3 p-3 rounded-xl border-2 border-transparent transition-all duration-200 hover:bg-gray-50"
          style={{
            background: isSelected
              ? "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F39A81, #959BEA) border-box"
              : "transparent",
          }}
        >
          <div className="text-gray-600">
            <svg
              width={isLandscape ? "40" : "24"}
              height={isLandscape ? "24" : "40"}
              viewBox={isLandscape ? "0 0 40 24" : "0 0 24 40"}
              fill="none"
            >
              <rect
                x="1"
                y="1"
                width={isLandscape ? "38" : "22"}
                height={isLandscape ? "22" : "38"}
                rx="3"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </div>
          <span className="text-base font-medium text-gray-700">
            {ratio.label}
          </span>
        </button>
      );
    }, [tempAspectRatio]);

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50">
        {/* Background Overlay */}
        <button
          type="button"
          className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Drawer Content */}
        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 transform translate-y-0 transition-transform duration-300 ease-out animate-in slide-in-from-bottom max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Prompt Settings</h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-full transition-colors bg-white"
            >
              <CloseIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Aspect Ratio Section */}
          {selectedModel !== 'Whisper' && !transcription && (
            <div className="mb-8">
              <h3 className="text-base text-left font-medium text-gray-900 mb-4">Aspect Ratio</h3>

              {/* Portrait and Landscape in 2 columns */}
              <div className='grid grid-cols-2 gap-6 mb-6'>
                {/* Portrait Section */}
                <div className="w-full">
                  <h4 className="text-sm font-medium text-gray-600 mb-3 text-left">Portrait</h4>
                  <div className="flex flex-col gap-2">
                    {portraitRatios.map((ratio) => renderAspectRatioButton(ratio))}
                  </div>
                </div>

                {/* Landscape Section */}
                <div className="w-full">
                  <h4 className="text-sm font-medium text-gray-600 mb-3 text-left">Landscape</h4>
                  <div className="flex flex-col gap-2">
                    {landscapeRatios.map((ratio) => renderAspectRatioButton(ratio))}
                  </div>
                </div>
              </div>

              {/* Square Section - Only for Image Models - Separate row */}
              {isImageModel && squareRatios.length > 0 && (
                <div className="w-full">
                  <h4 className="text-sm font-medium text-gray-600 mb-3 text-left">Square</h4>
                  <div className="flex flex-col gap-2">
                    {squareRatios.map((ratio) => renderAspectRatioButton(ratio))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Resolution Section - Only for Gemini 3 Pro */}
          {selectedModel === 'Gemini 3 Pro' && !transcription && (
            <div className="mb-8">
              <h3 className="text-base text-left font-medium text-gray-900 mb-4">Resolution</h3>
              <div className="grid grid-cols-3 gap-2">
                {['1k', '2k', '4k'].map((resolution) => (
                  <button
                    key={resolution}
                    onClick={() => setTempImageResolution(resolution)}
                    className="flex items-center justify-center p-3 rounded-xl border-2 border-transparent transition-all duration-200 hover:bg-gray-50"
                    style={{
                      background: tempImageResolution === resolution
                        ? "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F39A81, #959BEA) border-box"
                        : "transparent",
                    }}
                  >
                    <span className="text-base font-medium text-gray-700">
                      {resolution.toUpperCase()}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sound Section */}
          {!IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) && isAudioEnabled && (
            <div className="mb-8">
              <h3 className="text-base text-left font-medium text-gray-900 mb-4">Sound</h3>
              <div className="flex gap-3">
                {/* Audio On Button */}
                <button
                  onClick={() => setTempAudioEnabled(true)}
                  disabled={!isAudioEnabled}
                  className={`flex items-center justify-center p-4 rounded-xl border-2 border-transparent transition-all duration-200 hover:bg-gray-50 ${isAudioEnabled ? '' : 'opacity-50 cursor-not-allowed'
                    }`}
                  style={{
                    background: (tempAudioEnabled && isAudioEnabled)
                      ? "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F39A81, #959BEA) border-box"
                      : "transparent",
                  }}
                >
                  <VolumeUpIcon className="w-8 h-8 text-gray-600" />
                </button>

                {/* Audio Off Button */}
                <button
                  onClick={() => setTempAudioEnabled(false)}
                  disabled={!isAudioEnabled}
                  className={`flex items-center justify-center p-4 rounded-xl border-2 border-transparent transition-all duration-200 hover:bg-gray-50 ${isAudioEnabled ? '' : 'opacity-50 cursor-not-allowed'
                    }`}
                  style={{
                    background: (!tempAudioEnabled && isAudioEnabled)
                      ? "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F39A81, #959BEA) border-box"
                      : "transparent",
                  }}
                >
                  <VolumeOffIcon className="w-8 h-8 text-gray-600" />
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 py-3 px-6 border border-black text-gray-700 font-medium rounded-full bg-white transition-colors"
            >
              Reset
            </button>
            <button
              type='button'
              onClick={handleApply}
              className="flex-1 py-3 px-6 bg-gray-900 text-white font-medium rounded-full hover:bg-gray-800 transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

export const MobileModelSelector: FC<{
  orderedModels: string[];
  selectedModel: string;
  onModelSelect: (model: string) => void;
  isGenerating: boolean;
}> = ({ orderedModels, selectedModel, onModelSelect, isGenerating }) => {
  const { isOpen, setIsOpen, dropdownRef, handleItemClick } = useDropdown();

  return (
    <div className="flex flex-row items-center justify-between w-full px-4 py-2 rounded-full" style={{ backgroundColor: '#F0F7FF' }}>
      {/* Model Label */}
      <div className="text-gray-700 font-normal text-base">
        Model
      </div>

      {/* Dropdown Selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isGenerating}
          className="px-6 py-2 rounded-full font-inter transition-all duration-300 focus:outline-none text-gray-700 font-normal border-2 border-transparent bg-clip-padding min-w-[120px] text-center"
          style={{
            background: "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F39A81, #959BEA) border-box",
            fontFamily: 'Maven Pro',
            outline: 'none',
          }}
        >
          <span>{selectedModel}</span>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            className="absolute top-full right-0 mt-2 rounded-lg shadow-lg z-50 border-2 border-transparent bg-clip-padding"
            style={{
              background: "linear-gradient(white, white) padding-box, linear-gradient(#F39A81, #959BEA) border-box",
              border: '2px solid transparent',
              fontFamily: 'Maven Pro, sans-serif',
              width: '149px',
            }}
          >
            {orderedModels.map((model, index) => {
              const isDisabled = model === 'Hunyuan' || model === 'Mochi' || model === 'Opensora' || model === 'Wan 2.2';
              return (
                <button
                  key={model}
                  onClick={() => !isDisabled && handleItemClick(model, onModelSelect)}
                  disabled={isGenerating || isDisabled}
                  className={`w-full px-4 py-2 text-left transition-colors focus:outline-none ${
                    index === 0 ? 'rounded-t-lg' : ''
                  } ${
                    index === orderedModels.length - 1 ? 'rounded-b-lg' : ''
                  } ${
                    isDisabled ? 'cursor-not-allowed' : ''
                  }`}
                  style={{
                    outline: 'none',
                    background: selectedModel === model
                      ? "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box"
                      : "#FFFFFF",
                    border: selectedModel === model ? "2px solid transparent" : "none",
                    color: isDisabled ? '#9CA3AF' : '#374151', // gray-400 if disabled, gray-700 otherwise
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (selectedModel !== model && !isDisabled) {
                      e.currentTarget.style.background = "#F9FAFB";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedModel !== model && !isDisabled) {
                      e.currentTarget.style.background = "#FFFFFF";
                    }
                  }}
                >
                  {model}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
